import React, {
    ReactElement,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react'
import { AiSparkleIcon } from '@/components/icons/AiSparkleIcon'
import {
    Alert,
    Button,
    Input,
    message,
    Popconfirm,
    Tooltip,
    Typography,
} from 'antd'
import {
    atom,
    useRecoilState,
    useRecoilValue,
    useResetRecoilState,
    useSetRecoilState,
} from 'recoil'
import { ContentPath, hashContentPath } from 'common/src/ContentPath'
import {
    AgentContentJob,
    AgentContentJobMode,
    AgentContentJobStatus,
} from 'common/src/api/contentFrontendVsContentBackend/agentContent'
import { AnthropicModelName } from 'common/src/api/contentFrontendVsContentBackend/llm'
import {
    getAgentContentJob,
    listAgentContentJobsForSection,
    startAgentContentJob,
} from '@/api/cb/agentContent'
import { agentActivityAtom } from '@/atoms/agentActivity'
import {
    frontendDisplayedCourseSelector,
    isActiveCourseReadOnlySelector,
} from '@/atoms/auth'
import {
    formulaSheetEntriesHaveUnsavedChangesSelector,
    formulaSheetEntriesAtom,
    formulaSheetEntriesInvalidatorAtom,
    lastSavedFormulaSheetEntriesAtom,
} from '@/atoms/formulaSheetEntries'
import { hashedContentPathsWithSourceMaterialSelector } from '@/atoms/sourceMaterialTypes'
import { viewingStateAtom, ViewingStateType } from '@/atoms/viewingState'
import { getAgentModelLabel } from '@/components/GlobalAgentActivity/agentActivityLabels'
import { syncErrorMessage, syncSuccessMessage } from '@/utils/syncMessages'

export const FORMULA_SHEET_AGENT_MODEL = AnthropicModelName['claude-sonnet-5-5']

const formatRelativeTime = (isoTimestamp: string): string => {
    const deltaMs = Date.now() - new Date(isoTimestamp).getTime()
    const relative = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' })
    const minutes = Math.round(deltaMs / 60_000)
    if (minutes < 60) return relative.format(-minutes, 'minute')
    const hours = Math.round(minutes / 60)
    if (hours < 24) return relative.format(-hours, 'hour')
    return relative.format(-Math.round(hours / 24), 'day')
}

const isActiveStatus = (status: AgentContentJobStatus): boolean =>
    status === AgentContentJobStatus.queued ||
    status === AgentContentJobStatus.waitingForLock ||
    status === AgentContentJobStatus.running

interface FormulaSheetAgentSurfaceProps {
    contentPath: ContentPath
    entryCount: number
    isCourseFormulaJobActive: boolean
}

interface JustStartedJob {
    jobId: string | null
    createdAt: string
}

interface OptimisticFormulaSheetJob extends JustStartedJob {
    courseName: string
}

const optimisticFormulaSheetJobAtom = atom<OptimisticFormulaSheetJob | null>({
    key: 'optimisticFormulaSheetJobAtom',
    default: null,
})

export const FormulaSheetAgentSurface: React.FC<
    FormulaSheetAgentSurfaceProps
> = (props): ReactElement => {
    const courseName = useRecoilValue(frontendDisplayedCourseSelector)
    const isReadOnly = useRecoilValue(isActiveCourseReadOnlySelector)
    const activity = useRecoilValue(agentActivityAtom)
    const hasUnsavedChanges = useRecoilValue(
        formulaSheetEntriesHaveUnsavedChangesSelector
    )
    const setLastSavedEntries = useSetRecoilState(
        lastSavedFormulaSheetEntriesAtom
    )
    const invalidateEntries = useSetRecoilState(
        formulaSheetEntriesInvalidatorAtom
    )
    const resetFormulaSheetEntries = useResetRecoilState(
        formulaSheetEntriesAtom
    )
    const setViewingState = useSetRecoilState(viewingStateAtom)
    const [steeringText, setSteeringText] = useState<string>('')
    const [isStarting, setIsStarting] = useState<boolean>(false)
    const [justStarted, setJustStarted] = useState<JustStartedJob | null>(null)
    const [optimisticCourseJob, setOptimisticCourseJob] = useRecoilState(
        optimisticFormulaSheetJobAtom
    )
    const [trackedJobId, setTrackedJobId] = useState<string | null>(null)
    const [errorJobId, setErrorJobId] = useState<string | null>(null)
    const [history, setHistory] = useState<AgentContentJob[]>([])
    const groupHash = useMemo(
        () => hashContentPath(props.contentPath),
        [props.contentPath]
    )
    const isCourseWide = props.contentPath.length === 0
    const hashedPathsWithSourceMaterial = useRecoilValue(
        hashedContentPathsWithSourceMaterialSelector
    )
    // Section-scoped generation requires source material (the backend rejects
    // the start otherwise); course-wide passes work on existing entries
    const isMissingSourceMaterial =
        !isCourseWide && !hashedPathsWithSourceMaterial.has(groupHash)

    const openRunDetails = useCallback(
        (jobId: string): void => {
            setViewingState((current) => ({
                ...current,
                viewingStateType: ViewingStateType.agentActivity,
                id: jobId,
            }))
        },
        [setViewingState]
    )

    const refreshHistory = useCallback(async (): Promise<void> => {
        const response = await listAgentContentJobsForSection({
            courseName,
            contentPath: props.contentPath,
        })
        if (response.data.success) {
            setHistory(
                response.data.payload.jobs
                    .filter(
                        (job) => job.mode === AgentContentJobMode.formulaSheet
                    )
                    .sort(
                        (left, right) =>
                            new Date(right.createdAt).getTime() -
                            new Date(left.createdAt).getTime()
                    )
            )
        }
    }, [courseName, props.contentPath])

    useEffect(() => {
        void refreshHistory()
    }, [refreshHistory])

    const activeGroupJob = useMemo(
        () =>
            activity?.activeJobs.find(
                (job) =>
                    job.mode === AgentContentJobMode.formulaSheet &&
                    job.courseName === courseName &&
                    hashContentPath(job.contentPath) === groupHash &&
                    isActiveStatus(job.status)
            ) ?? null,
        [activity?.activeJobs, courseName, groupHash]
    )

    useEffect(() => {
        if (!activeGroupJob) return
        setTrackedJobId(activeGroupJob.jobId)
        setJustStarted(null)
        setOptimisticCourseJob(null)
        setErrorJobId(null)
    }, [activeGroupJob, setOptimisticCourseJob])

    useEffect(() => {
        if (!activity || activeGroupJob) return
        const jobId = trackedJobId ?? justStarted?.jobId ?? null
        if (!jobId) return
        let isMounted = true
        void (async (): Promise<void> => {
            const response = await getAgentContentJob({ jobId })
            if (!isMounted || !response.data.success) return
            const completedJob = response.data.payload.job
            if (isActiveStatus(completedJob.status)) return
            setTrackedJobId(null)
            setJustStarted(null)
            setOptimisticCourseJob(null)
            if (completedJob.status === AgentContentJobStatus.succeeded) {
                setErrorJobId(null)
                // Clear the stale baseline before the async formula data is
                // invalidated; the initializer then primes the fetched result.
                setLastSavedEntries([])
                resetFormulaSheetEntries()
                invalidateEntries((value) => value + 1)
                syncSuccessMessage('Formula sheet entries updated by agent.')
            } else {
                setErrorJobId(jobId)
            }
            void refreshHistory()
        })()
        return () => {
            isMounted = false
        }
    }, [
        activeGroupJob,
        activity,
        invalidateEntries,
        justStarted?.jobId,
        refreshHistory,
        resetFormulaSheetEntries,
        setLastSavedEntries,
        setOptimisticCourseJob,
        trackedJobId,
    ])

    const startJob = useCallback(async (): Promise<void> => {
        setIsStarting(true)
        setErrorJobId(null)
        setJustStarted({ jobId: null, createdAt: new Date().toISOString() })
        setOptimisticCourseJob({
            courseName,
            jobId: null,
            createdAt: new Date().toISOString(),
        })
        try {
            const response = await startAgentContentJob({
                courseName,
                contentPath: props.contentPath,
                mode: AgentContentJobMode.formulaSheet,
                modelName: FORMULA_SHEET_AGENT_MODEL,
                instructorRequest: steeringText.trim() || undefined,
            })
            if (!response.data.success) {
                setJustStarted(null)
                setOptimisticCourseJob(null)
                syncErrorMessage(
                    `Failed to start formula sheet agent: ${response.data.error}`
                )
                return
            }
            const jobId = response.data.payload.jobId
            setTrackedJobId(jobId)
            setJustStarted((current) => ({
                jobId,
                createdAt: current?.createdAt ?? new Date().toISOString(),
            }))
            setOptimisticCourseJob((current) => ({
                courseName,
                jobId,
                createdAt: current?.createdAt ?? new Date().toISOString(),
            }))
            message.success({
                content: (
                    <span>
                        {response.data.payload.waitingOnJobId
                            ? 'Run queued behind another job — it starts automatically when that run finishes. '
                            : 'Formula sheet agent started. '}
                        <Button
                            type={'link'}
                            size={'small'}
                            onClick={() => openRunDetails(jobId)}
                        >
                            View run
                        </Button>
                    </span>
                ),
                duration: 8,
            })
        } finally {
            setIsStarting(false)
        }
    }, [
        courseName,
        openRunDetails,
        props.contentPath,
        setOptimisticCourseJob,
        steeringText,
    ])

    const latestJob = history[0]
    const latestEndedAt = latestJob?.attempts
        .map((attempt) => attempt.endedAt)
        .filter((endedAt): endedAt is string => !!endedAt)
        .pop()
    const displayedJob = activeGroupJob
        ? {
              createdAt: activeGroupJob.startedAt ?? activeGroupJob.createdAt,
              jobId: activeGroupJob.jobId,
              status: activeGroupJob.status,
          }
        : justStarted
          ? {
                createdAt: justStarted.createdAt,
                jobId: justStarted.jobId,
                status: AgentContentJobStatus.queued,
            }
          : null
    const disabledReason = isMissingSourceMaterial
        ? 'This section has no source material — upload source material for it before running the agent'
        : hasUnsavedChanges
          ? 'Save the formula sheet before running the agent'
          : isReadOnly
            ? 'You have read-only access to this course'
            : props.isCourseFormulaJobActive ||
                displayedJob ||
                optimisticCourseJob?.courseName === courseName
              ? 'A formula sheet agent is already running for this course'
              : undefined
    const button = isCourseWide ? (
        <Popconfirm
            title={'Run a course-wide formula sheet pass?'}
            description={
                'The agent will dedupe entries across sections, attach unassigned entries, normalize formatting, and set page breaks. A checkpoint is taken first.'
            }
            okText={'Run pass'}
            onConfirm={() => void startJob()}
            disabled={!!disabledReason}
        >
            <Button disabled={!!disabledReason} loading={isStarting}>
                <AiSparkleIcon /> Course-wide agent pass
            </Button>
        </Popconfirm>
    ) : props.entryCount ? (
        <Popconfirm
            title={'Update this section with the agent?'}
            description={
                "The agent may revise, add, or remove this section's formula entries; entries it leaves unchanged are kept exactly as they are. A checkpoint is taken first."
            }
            okText={'Update'}
            onConfirm={() => void startJob()}
            disabled={!!disabledReason}
        >
            <Button disabled={!!disabledReason} loading={isStarting}>
                <AiSparkleIcon /> Update with agent
            </Button>
        </Popconfirm>
    ) : (
        <Button
            type={'primary'}
            disabled={!!disabledReason}
            loading={isStarting}
            onClick={() => void startJob()}
        >
            Generate with agent
        </Button>
    )

    return (
        <div style={{ paddingTop: '0.75rem' }}>
            {!displayedJob && !errorJobId && latestJob && latestEndedAt && (
                <Alert
                    type={
                        latestJob.status === AgentContentJobStatus.succeeded
                            ? 'success'
                            : 'warning'
                    }
                    showIcon
                    style={{ marginBottom: '0.75rem' }}
                    message={
                        <Typography.Text>
                            {`Last agent run: ${latestJob.status} · ${getAgentModelLabel(latestJob.modelName)} · by ${latestJob.requestedByDisplayName ?? 'unknown'} · ${formatRelativeTime(latestEndedAt)} `}
                            <Button
                                type={'link'}
                                size={'small'}
                                onClick={() => openRunDetails(latestJob.id)}
                            >
                                View run
                            </Button>
                        </Typography.Text>
                    }
                    description={
                        latestJob.instructorRequest ? (
                            <Typography.Text italic>
                                {`"${latestJob.instructorRequest}"`}
                            </Typography.Text>
                        ) : undefined
                    }
                />
            )}
            <Input.TextArea
                placeholder={'Optional guidance for the formula sheet agent...'}
                value={steeringText}
                onChange={(event) => setSteeringText(event.target.value)}
                autoSize={{ minRows: 1, maxRows: 4 }}
                style={{ marginBottom: '0.5rem' }}
                disabled={!!displayedJob}
            />
            <Tooltip title={disabledReason} placement={'right'}>
                <span>{button}</span>
            </Tooltip>
            {displayedJob && (
                <Alert
                    type={'info'}
                    showIcon
                    style={{ marginTop: '0.75rem' }}
                    message={`Formula sheet · ${getAgentModelLabel(FORMULA_SHEET_AGENT_MODEL)}`}
                    description={
                        <Typography.Text>
                            {displayedJob.status ===
                            AgentContentJobStatus.waitingForLock
                                ? 'waiting for another run to finish'
                                : displayedJob.status}{' '}
                            · created{' '}
                            {formatRelativeTime(displayedJob.createdAt)}{' '}
                            {displayedJob.jobId && (
                                <Button
                                    type={'link'}
                                    size={'small'}
                                    onClick={() =>
                                        openRunDetails(
                                            displayedJob.jobId as string
                                        )
                                    }
                                >
                                    View run
                                </Button>
                            )}
                        </Typography.Text>
                    }
                />
            )}
            {errorJobId && (
                <Alert
                    type={'error'}
                    showIcon
                    style={{ marginTop: '0.75rem' }}
                    message={'Formula sheet agent did not complete.'}
                    description={
                        <Button
                            type={'link'}
                            size={'small'}
                            onClick={() => openRunDetails(errorJobId)}
                        >
                            View run
                        </Button>
                    }
                />
            )}
        </div>
    )
}
